import {
  FormattedMessage,
  injectIntl,
  type intlFormatPropTypes,
} from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ps-design-system/components/Dialog";
import { Text } from "@ps-design-system/components/Text";

import { TAX_DETAILS_MESSAGES } from "./TaxDetails.messages";
import { formatRegistrationTaxTypeName } from "./TaxDetails.utils";
import { useTaxDetails } from "./TaxDetailsProvider.tsx";

type TaxDetailsDeleteModalProps = {
  intl: intlFormatPropTypes;
};

const TaxDetailsDeleteModalComponent = ({
  intl,
}: TaxDetailsDeleteModalProps) => {
  const {
    closeDeleteModal,
    deleteVatDetails,
    hasDeleteFailed,
    isDeleting,
    registrationIdToDelete,
    registrationToDelete,
  } = useTaxDetails();

  // Names the tax the guest is actually removing — "Remove GST", not "Remove
  // VAT". Falls back to generic copy for the frame between closing the modal
  // and the registration list settling.
  const taxTypeName = formatRegistrationTaxTypeName({
    intl,
    registration: registrationToDelete ?? { taxTypes: [] },
  });

  /**
   * Ignores dismissal while the delete is in flight, so the guest can't close
   * the modal on a request that still lands.
   */
  const handleOpenChange = () => {
    if (!isDeleting) {
      closeDeleteModal();
    }
  };

  return (
    <Dialog
      open={Boolean(registrationIdToDelete)}
      onOpenChange={handleOpenChange}
    >
      <DialogContent size="md">
        <DialogHeader headerAlignment="left" showCloseButton>
          <DialogTitle className="tw-m-0">
            <FormattedMessage
              {...TAX_DETAILS_MESSAGES.removeHeading}
              values={{ taxType: taxTypeName }}
            />
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Text typeface="body-2">
            <FormattedMessage
              {...TAX_DETAILS_MESSAGES.removeBody}
              values={{ taxType: taxTypeName }}
            />
          </Text>
          {hasDeleteFailed && (
            <Text className="tw-text-red-800" element="p" typeface="body-1">
              <FormattedMessage
                id="tax_details.delete_modal.error"
                defaultMessage="Something went wrong, please try again. If the problem persists please refresh."
              />
            </Text>
          )}
        </DialogBody>
        <DialogFooter>
          <div className="tw-flex tw-justify-between tw-w-full">
            <Button
              variant="underline"
              onClick={closeDeleteModal}
              disabled={isDeleting}
              testId="tax-details-delete-modal-go-back"
            >
              <FormattedMessage
                id="tax_details.delete_modal.go_back"
                defaultMessage="Go back"
              />
            </Button>
            <Button
              onClick={deleteVatDetails}
              disabled={isDeleting}
              showSpinner={isDeleting}
            >
              <FormattedMessage
                {...TAX_DETAILS_MESSAGES.removeConfirm}
                values={{ taxType: taxTypeName }}
              />
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TaxDetailsDeleteModal = injectIntl(TaxDetailsDeleteModalComponent);

export { TaxDetailsDeleteModal };
